import type * as React from 'react';
import { cn } from '../../lib/cn.ts';

export interface SettingsSectionProps extends Omit<
  React.ComponentProps<'section'>,
  'style' | 'title'
> {
  /* Grey uppercase micro-label. Never an icon beside it. */
  title?: string;
  /* Sits opposite the title — a read-only badge, a count, a link out. */
  action?: React.ReactNode;
  /* One line under the title, before the rows. */
  description?: string;
}

/*
 * A card of related settings rows.
 *
 * Rows are divided by their own top borders rather than by a divider element,
 * which is why the card clips its corners: the first row's border would
 * otherwise cut across the rounded edge.
 */
export function SettingsSection({
  title,
  action,
  description,
  className,
  children,
  ...props
}: SettingsSectionProps) {
  return (
    <section
      data-slot='settings-section'
      className={cn(
        'mb-4 overflow-hidden rounded-lg border border-line bg-surface',
        className,
      )}
      {...props}
    >
      <SettingsSectionHeading title={title} action={action} description={description} />
      {/*
        Rows live in their own wrapper so "first row draws no divider" is a
        first-child rule. Left as siblings of the header it would need
        :first-of-type, which does not mean what it looks like it means here —
        the header is a div too, so no row would ever match.
      */}
      <div>{children}</div>
    </section>
  );
}

export type SettingsSectionHeadingProps = Pick<
  SettingsSectionProps,
  'title' | 'action' | 'description'
>;

/*
 * A section's title, action and description. Also heads a block inside one
 * card, where a few short lists share a card rather than each taking its own.
 */
export function SettingsSectionHeading({
  title,
  action,
  description,
}: SettingsSectionHeadingProps) {
  return (
    <>
      {(title || action) && (
        <div className='flex items-start justify-between gap-4 px-5.5 pt-4.5 pb-1.5'>
          {title && (
            <h2 className='text-xs font-bold tracking-widest text-ink-muted uppercase'>
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      {description && (
        <p className='px-5.5 pt-2 pb-1 text-sm text-ink-muted'>{description}</p>
      )}
    </>
  );
}
